import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Printer, Truck, Package, CheckCircle, X, ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Three-step combo action for an admin order card:
 *   1. Print the invoice (opens the existing InvoiceModal in the parent)
 *   2. Assign a driver OR mujahez (single-pick)
 *   3. Confirm the order → status becomes "confirmed"
 *
 * Replaces the old workflow of (a) confirm → (b) navigate to confirmed
 * orders → (c) print, by chaining all three operations into a single
 * guided flow the admin can complete with three clicks.
 */
const OrderComboActionModal = ({
  open,
  order,
  drivers = [],
  mujahez = [],
  onPrint,         // (order) => parent shows the existing InvoiceModal
  onClose,
  onComplete,      // () => parent refreshes orders list
}) => {
  const [step, setStep] = useState(1);
  const [chosenDriverId, setChosenDriverId] = useState("");
  const [chosenMujahezId, setChosenMujahezId] = useState("");
  const [working, setWorking] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("jumlaty_token") : "";
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  if (!open || !order) return null;

  const reset = () => {
    setStep(1);
    setChosenDriverId("");
    setChosenMujahezId("");
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const doPrint = () => {
    // Delegate to parent. Parent opens its existing InvoiceModal which has
    // the print button + WhatsApp share + full layout — no need to
    // duplicate that here.
    onPrint?.(order);
  };

  const doAssign = async () => {
    try {
      if (chosenDriverId) {
        await axios.patch(`${API}/admin/orders/${order.id}/assign`, { driver_id: chosenDriverId }, auth);
      } else if (chosenMujahezId) {
        // Use the mujahez assignment endpoint (no discount in the combo flow)
        await axios.patch(`${API}/admin/orders/${order.id}/assign-mujahez`,
          { mujahez_id: chosenMujahezId }, auth);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التعيين");
      throw e;
    }
  };

  const doConfirm = async () => {
    setWorking(true);
    try {
      if (chosenDriverId || chosenMujahezId) {
        await doAssign();
      }
      await axios.patch(`${API}/admin/orders/${order.id}`, { status: "confirmed" }, auth);
      toast.success("تم تنفيذ الإجراء كاملاً — الطلب موجود في الفواتير المؤكدة الآن");
      onComplete?.();
      close();
    } catch (e) {
      if (!e?.response) toast.error("فشل تأكيد الطلب");
    } finally {
      setWorking(false);
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-1 mb-5">
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex items-center gap-1">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold transition-colors ${
              n <= step ? "bg-[#15803D] text-white" : "bg-[#F4F1E8] text-[#8B7E5A]"
            }`}
          >{n}</div>
          {n < 3 && <div className={`w-8 h-0.5 ${n < step ? "bg-[#15803D]" : "bg-[#E8D9B5]"}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[140] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={close}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-white rounded-3xl max-w-lg w-full p-6 relative shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          dir="rtl"
          data-testid="order-combo-modal"
        >
          <button
            onClick={close}
            data-testid="order-combo-close"
            className="absolute top-3 left-3 w-8 h-8 rounded-full bg-[#F4F1E8] hover:bg-[#E8D9B5] flex items-center justify-center text-[#2D4A3E]"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="text-center mb-4">
            <div className="text-xs text-[#5C6E64]">إجراء سريع</div>
            <h3 className="text-lg font-extrabold text-[#111814]">
              طلب {order.user_name || "زبون"}
              <span className="text-[#5C6E64] text-sm mr-2" dir="ltr">#{(order.id || "").slice(0, 8).toUpperCase()}</span>
            </h3>
          </div>

          <StepIndicator />

          {/* Step 1 — Print */}
          {step === 1 && (
            <div className="space-y-3 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#1E3A5F] text-white flex items-center justify-center shadow-lg relative">
                <Printer className="w-8 h-8" />
                {/* Print status dot on the icon itself */}
                <span
                  className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ring-2 ring-white ${
                    order?.printed_at
                      ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]"
                      : "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.9)] animate-pulse"
                  }`}
                  data-testid={`combo-print-status-${order?.id || "x"}`}
                  aria-label={order?.printed_at ? "مطبوعة" : "غير مطبوعة"}
                />
              </div>
              <h4 className="font-extrabold text-base text-[#111814]">
                الخطوة 1: طباعة الفاتورة
                {order?.printed_at && (
                  <span className="ml-2 text-xs font-bold text-emerald-600">✓ مطبوعة</span>
                )}
              </h4>
              <p className="text-sm text-[#5C6E64]">اضغط (طباعة) لفتح الفاتورة، ثم اضغط (التالي).</p>
              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={doPrint}
                  data-testid="combo-step1-print"
                  className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-extrabold py-2.5 rounded-2xl flex items-center justify-center gap-2 relative"
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full ring-2 ring-white/70 ${
                      order?.printed_at
                        ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]"
                        : "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.9)] animate-pulse"
                    }`}
                  />
                  <Printer className="w-4 h-4" /> طباعة الفاتورة
                </button>
                <button
                  onClick={() => setStep(2)}
                  data-testid="combo-step1-next"
                  className="bg-[#15803D] hover:bg-[#126B32] text-white font-extrabold py-2.5 rounded-2xl flex items-center justify-center gap-2"
                >
                  التالي <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — Assign Driver OR Mujahez */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#7ED321] text-white flex items-center justify-center shadow-lg mb-2">
                  <Truck className="w-8 h-8" />
                </div>
                <h4 className="font-extrabold text-base text-[#111814]">الخطوة 2: تعيين السائق أو المجهّز</h4>
                <p className="text-xs text-[#5C6E64] mt-1">اختر سائق مباشر أو مجهّز. (اختياري — يمكن تخطّيها)</p>
              </div>

              {drivers.length > 0 && (
                <div>
                  <label className="text-xs font-bold text-[#5C6E64] block mb-1">🚚 السائق</label>
                  <select
                    value={chosenDriverId}
                    onChange={(e) => { setChosenDriverId(e.target.value); setChosenMujahezId(""); }}
                    data-testid="combo-step2-driver"
                    className="w-full px-3 py-2 rounded-2xl bg-[#E0F2FE] border border-[#4A90E2]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A90E2]"
                  >
                    <option value="">— لا أحد —</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.full_name} ({d.active_orders || 0} نشطة)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {mujahez.length > 0 && (
                <div>
                  <label className="text-xs font-bold text-[#5C6E64] block mb-1">📦 المجهّز</label>
                  <select
                    value={chosenMujahezId}
                    onChange={(e) => { setChosenMujahezId(e.target.value); setChosenDriverId(""); }}
                    data-testid="combo-step2-mujahez"
                    className="w-full px-3 py-2 rounded-2xl bg-[#F0FDF4] border border-[#7ED321]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#7ED321]"
                  >
                    <option value="">— لا أحد —</option>
                    {mujahez.map((m) => (
                      <option key={m.id} value={m.id}>{m.full_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setStep(1)}
                  data-testid="combo-step2-back"
                  className="px-4 bg-[#F4F1E8] text-[#2D4A3E] font-bold rounded-2xl flex items-center gap-1"
                >
                  <ArrowRight className="w-4 h-4" /> السابق
                </button>
                <button
                  onClick={() => setStep(3)}
                  data-testid="combo-step2-next"
                  className="flex-1 bg-[#15803D] hover:bg-[#126B32] text-white font-extrabold py-2.5 rounded-2xl flex items-center justify-center gap-2"
                >
                  التالي <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — Confirm */}
          {step === 3 && (
            <div className="space-y-3 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#15803D] text-white flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h4 className="font-extrabold text-base text-[#111814]">الخطوة 3: تأكيد الطلب</h4>
              <div className="bg-[#F0FDF4] border border-[#7ED321]/40 rounded-2xl p-3 text-right">
                <div className="text-xs text-[#2D4A3E] space-y-1">
                  <div>📄 الفاتورة: تم تجهيزها للطباعة</div>
                  {chosenDriverId && (
                    <div>🚚 السائق: <strong>{drivers.find((d) => d.id === chosenDriverId)?.full_name}</strong></div>
                  )}
                  {chosenMujahezId && (
                    <div>📦 المجهّز: <strong>{mujahez.find((m) => m.id === chosenMujahezId)?.full_name}</strong></div>
                  )}
                  {!chosenDriverId && !chosenMujahezId && (
                    <div className="text-[#a06e10]">⚠️ لم يتم تعيين سائق أو مجهّز.</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#5C6E64]">سيُنقل الطلب إلى "الفواتير المؤكدة" بعد التأكيد.</p>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setStep(2)}
                  disabled={working}
                  data-testid="combo-step3-back"
                  className="px-4 bg-[#F4F1E8] text-[#2D4A3E] font-bold rounded-2xl flex items-center gap-1 disabled:opacity-60"
                >
                  <ArrowRight className="w-4 h-4" /> السابق
                </button>
                <button
                  onClick={doConfirm}
                  disabled={working}
                  data-testid="combo-step3-confirm"
                  className="flex-1 bg-[#E85D45] hover:bg-[#cf4933] text-white font-extrabold py-2.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {working ? "جاري التأكيد..." : <><CheckCircle className="w-4 h-4" /> تأكيد الطلب</>}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OrderComboActionModal;
